import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { isLocalEnvironment } from '../../config/environment.config';

interface LocationState {
  from?: {
    pathname: string;
  };
}

const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = location.state as LocationState;
  const from = state?.from?.pathname || '/';
  const isLocal = isLocalEnvironment();

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true);
      setError(null);
      
      if (isLocal) {
        // For local environment, directly call login which will bypass Okta
        await login();
        // Navigate to the intended destination or home
        navigate(from, { replace: true });
      } else {
        // For dev and prod environments, store the intended destination and use Okta
        if (from !== '/') {
          sessionStorage.setItem('oktaOriginalUri', from);
        }
        
        // Initiate OKTA login redirect
        await login();
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed. Please try again.');
      setIsLoggingIn(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>MI Revenue Analytics Platform</h1>
        </div>

        {error && (
          <div style={styles.errorAlert}>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        <div style={styles.content}>
          {isLocal && (
            <div style={styles.infoBox}>
              <p style={styles.infoTitle}>🏠 Local Development Mode</p>
              <p style={styles.infoText}>
                You're running in local development mode. Click the button below to bypass Okta authentication and proceed directly to the dashboard.
              </p>
            </div>
          )}
          
          <button
            onClick={handleLogin}
            disabled={isLoggingIn}
            style={{
              ...styles.loginButton,
              ...(isLoggingIn ? styles.loginButtonDisabled : {}),
            }}
          >
            {isLoggingIn ? (
              <div style={styles.buttonContent}>
                <div style={styles.buttonSpinner} />
                {isLocal ? 'Signing in...' : 'Redirecting to OKTA...'}
              </div>
            ) : (
              isLocal ? 'Sign in (Local Development)' : 'Sign in with OKTA'
            )}
          </button>
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Having trouble signing in? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '1rem',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    maxWidth: '400px',
    width: '100%',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '2rem',
  },
  title: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.75rem',
    fontWeight: 'bold' as const,
    color: '#000000',
  },
  subtitle: {
    margin: 0,
    fontSize: '1rem',
    color: '#666',
  },
  content: {
    marginBottom: '2rem',
  },
  loginButton: {
    width: '100%',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 'bold' as const,
    color: 'white',
    backgroundColor: '#D6002A',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    marginBottom: '1.5rem',
  },
  loginButtonDisabled: {
    backgroundColor: '#6c757d',
    cursor: 'not-allowed',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  loadingText: {
    textAlign: 'center' as const,
    color: '#666',
    margin: 0,
  },
  errorAlert: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  errorText: {
    margin: 0,
    color: '#721c24',
    fontSize: '0.9rem',
  },
  infoBox: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '1rem',
  },
  infoTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1rem',
    fontWeight: 'bold' as const,
    color: '#333',
  },
  infoText: {
    margin: 0,
    fontSize: '0.85rem',
    color: '#666',
    lineHeight: 1.4,
  },
  footer: {
    textAlign: 'center' as const,
    borderTop: '1px solid #e9ecef',
    paddingTop: '1rem',
  },
  footerText: {
    margin: 0,
    fontSize: '0.8rem',
    color: '#999',
  },
};

export default LoginPage;